import { AutomaticCondition } from 'interfaces/automaticCondition';
import {
  ModelInference,
  NumberOption,
  OrchestrationData,
  OrchestrationDataType,
  orchestrationDataTypeNumbers,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import PostProcessingAction, {
  InputSource,
  InputType,
  MappingFilterMetadata,
  PostProcessingActionCreated,
  Input as PostProcessingActionInput,
} from 'interfaces/postProcessingAction';
import {
  cloneDeep,
  Dictionary,
  every,
  filter,
  find,
  findKey,
  first,
  forEach,
  get,
  groupBy,
  includes,
  indexOf,
  isEmpty,
  isEqual,
  isObject,
  map,
  set,
  size,
  some,
  uniq,
} from 'lodash';
import { AssignmentByStain } from 'utils/assignmentManagement';
import { OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { RegistrationDetailsWithStains, validateTargetStainRegistrations } from 'utils/slideRegistrations';
import { v4 as uuidv4 } from 'uuid';
import { NewCell } from '.';
import { getNameByValue } from './FeaturesStep/NewCellsStain';
import { targetCellClassesInputKey } from './PostProcessingActions/PostProcessingAction';
import {
  convertLogicalQueryFromBackend,
  convertLogicalQueryToBackend,
  getInputType,
  getSpecificInputError,
  isFeatureBetweenStains,
} from './PostProcessingActions/utils';
import { NEIGHBOR_STAINS_INPUT_KEY, TARGET_STAINS_INPUT_KEY } from './consts';

/**
 * Helper function to convert logical query from backend format with consistent error handling and logging
 */
const convertLogicalQueryFromBackendWrapper = (
  logicalQueryValue: any,
  actionName: string,
  inputKey: string,
  mappingFiltersMetadataForLogicalQuery: MappingFilterMetadata[],
  contextDescription = ''
) => {
  if (!logicalQueryValue) {
    return { shouldUpdate: false, value: logicalQueryValue };
  }

  try {
    const convertedQuery = convertLogicalQueryFromBackend(logicalQueryValue, mappingFiltersMetadataForLogicalQuery);
    return { shouldUpdate: true, value: convertedQuery };
  } catch (error) {
    console.warn(
      `Failed to convert logical query for ${contextDescription}action ${actionName} input ${inputKey}:`,
      error,
      { logicalQueryValue }
    );
    // Keep the original value if conversion fails - no update needed since it's already there
    return { shouldUpdate: false, value: logicalQueryValue };
  }
};

// Combine the orchestration data from the previous and new data, if the data already exists, combine the options
// see examples in utils.spec.ts
export const combineOrchestrationMetadata = (
  orchestrationsData: OrchestrationData[],
  newData: OrchestrationData[]
): OrchestrationData[] => {
  if (isEmpty(orchestrationsData)) return newData;

  const combinedOrchestrationsData = cloneDeep(orchestrationsData);

  forEach(newData, (data) => {
    const existingData = find(orchestrationsData, { label: data.label, type: data.type });
    const existingDataLabel = find(orchestrationsData, { label: data.label });

    if (existingDataLabel && !existingData) {
      console.warn(
        `Orchestration data with label ${data.label} already exists with different type. Existing type: ${existingDataLabel.type}, new type: ${data.type}. Value will be ignored to prevent duplications in the select.`
      );
      return;
    }

    if (existingData) {
      const combinedOrchestrationData = cloneDeep(existingData);
      if (data?.options) {
        if (data?.type === OrchestrationDataType.Categorical) {
          combinedOrchestrationData.options = uniq([
            ...(combinedOrchestrationData.options as string[]),
            ...(data.options as string[]),
          ]);
        } else if (includes(orchestrationDataTypeNumbers, data?.type)) {
          const dataOptions = data?.options as NumberOption;
          const combinedOrchestrationDataOptions = combinedOrchestrationData?.options as NumberOption;
          combinedOrchestrationData.options = {
            min: Math.max(dataOptions?.min, combinedOrchestrationDataOptions?.min),
            max: Math.min(dataOptions?.max, combinedOrchestrationDataOptions?.max),
          };
        }
      }
      combinedOrchestrationsData[indexOf(orchestrationsData, existingData)] = combinedOrchestrationData;
    } else {
      combinedOrchestrationsData.push(data);
    }
  });

  return combinedOrchestrationsData;
};

export const convertFeatureActionsToBackend = (
  postProcessingActionsById: Dictionary<PostProcessingAction>,
  featureActions: PostProcessingActionCreated[],
  newCellsCreated: Record<string, NewCell>
) => {
  return map(featureActions, (action) => {
    let newAction = cloneDeep(action);
    let actionModified = false;

    // First, handle logical query conversion to backend format
    forEach(postProcessingActionsById?.[action.actionId]?.inputs, (input) => {
      if (input.inputType === InputType.LOGICAL_QUERY) {
        const logicalQueryValue = get(action, input.inputKey);
        if (logicalQueryValue) {
          try {
            const convertedLogicalQuery = convertLogicalQueryToBackend(logicalQueryValue);
            set(newAction, input.inputKey, convertedLogicalQuery);
            actionModified = true;
          } catch (error) {
            console.warn(
              `Failed to convert feature logical query to backend for action ${action.actionName} input ${input.inputKey}:`,
              error
            );
          }
        }
      }

      // Handle GROUP inputs that might contain logical queries (similar to postprocessing)
      if (input.inputType === InputType.GROUP) {
        forEach(input.children, (childInput) => {
          if (childInput.inputType === InputType.LOGICAL_QUERY) {
            const parentInputKey = input.inputKey;
            const parentArray = get(newAction, parentInputKey);
            if (parentArray) {
              forEach(parentArray, (parentItem, index) => {
                const childValue = get(parentItem, childInput.inputKey);
                if (childValue) {
                  try {
                    const convertedLogicalQuery = convertLogicalQueryToBackend(childValue);
                    set(newAction[input.inputKey][index], childInput.inputKey, convertedLogicalQuery);
                    actionModified = true;
                  } catch (error) {
                    console.warn(
                      `Failed to convert feature group logical query to backend for action ${action.actionName} child input ${childInput.inputKey}:`,
                      error
                    );
                  }
                }
              });
            }
          }
        });
      }
    });

    // Then, handle intermediate cells input conversion
    const intermediateCellsInput = filter(postProcessingActionsById?.[action.actionId]?.inputs, {
      inputSource: InputSource.INTERMEDIATE_CELLS_FEATURES_PER_TARGET_STAIN,
    });

    if (!isEmpty(intermediateCellsInput)) {
      forEach(intermediateCellsInput, (input) => {
        const inputType = getInputType(input, action);
        if (inputType === InputType.SELECT) {
          const selectedValue = get(newAction, input.inputKey);
          if (newCellsCreated[selectedValue]?.value) {
            set(newAction, input.inputKey, convertLogicalQueryToBackend(newCellsCreated[selectedValue].value));
            actionModified = true;
          }
        } else if (inputType === InputType.MULTISELECT) {
          const selectedValues = get(newAction, input.inputKey);
          const newValue = map(selectedValues, (selectedOption) => {
            if (newCellsCreated[selectedOption]?.value) {
              return convertLogicalQueryToBackend(newCellsCreated[selectedOption].value);
            }
            return selectedOption;
          });
          set(newAction, input.inputKey, newValue);
          actionModified = true;
        }
      });
    }

    return actionModified ? newAction : action;
  });
};

export const convertPostProcessingActionsToBackend = (
  postProcessingActionsById: Dictionary<PostProcessingAction>,
  postProcessingActions: PostProcessingActionCreated[]
) => {
  return map(postProcessingActions, (action) => {
    const updatedAction = cloneDeep(action);
    forEach(postProcessingActionsById?.[action.actionId]?.inputs, (input) => {
      if (input.inputType === InputType.LOGICAL_QUERY) {
        const convertedLogicalQuery = convertLogicalQueryToBackend(get(action, input.inputKey));
        set(updatedAction, input.inputKey, convertedLogicalQuery);
      }

      if (input.inputType === InputType.GROUP) {
        // If the input is a group, we need to convert each logical query input in the group
        forEach(input.children, (childInput) => {
          if (childInput.inputType === InputType.LOGICAL_QUERY) {
            const parentInputKey = input.inputKey;
            const parentArray = get(updatedAction, parentInputKey);
            forEach(parentArray, (parentItem, index) => {
              const childValue = get(parentItem, childInput.inputKey);
              const convertedLogicalQuery = convertLogicalQueryToBackend(childValue);
              set(updatedAction[input.inputKey][index], childInput.inputKey, convertedLogicalQuery);
            });
          }
        });
      }
    });

    return updatedAction;
  });
};

export const convertPostProcessingActionsFromBackend = (
  postProcessingActionsById: Dictionary<PostProcessingAction>,
  postProcessingActions: PostProcessingActionCreated[],
  getMappingFiltersMetadataForLogicalQuery: (
    stain: string,
    index: number,
    postProcessingActions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    actionAttributesTable: AttributesTable
  ) => any,
  getOptionsByValue: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => Dictionary<{ id: string | number; label: string }>
) => {
  return map(postProcessingActions, (action, index) => {
    const newAction = cloneDeep(action);

    forEach(postProcessingActionsById?.[action.actionId]?.inputs, (input) => {
      if (input.inputType === InputType.LOGICAL_QUERY) {
        const logicalQueryValue = get(action, input.inputKey);
        const mappingFiltersMetadata = getMappingFiltersMetadataForLogicalQuery(
          action?.stain,
          index,
          postProcessingActions,
          action.actionInput,
          postProcessingActionsById,
          input.attributesTable ?? AttributesTable.CellTable
        );

        const conversionResult = convertLogicalQueryFromBackendWrapper(
          logicalQueryValue,
          action.actionName,
          input.inputKey,
          mappingFiltersMetadata
        );

        if (conversionResult.shouldUpdate) {
          set(newAction, input.inputKey, conversionResult.value);
        }
      }
      if (input.inputType === InputType.GROUP) {
        // If the input is a group, we need to convert each logical query input in the group
        forEach(input.children, (childInput) => {
          if (childInput.inputType === InputType.LOGICAL_QUERY) {
            forEach(get(action, input.inputKey), (childInputValue, childIndex) => {
              const logicalQueryValue = get(childInputValue, childInput.inputKey);
              const mappingFiltersMetadata = getMappingFiltersMetadataForLogicalQuery(
                action?.stain,
                index,
                postProcessingActions,
                action.actionInput,
                postProcessingActionsById,
                childInput.attributesTable ?? AttributesTable.CellTable
              );

              const conversionResult = convertLogicalQueryFromBackendWrapper(
                logicalQueryValue,
                action.actionName,
                childInput.inputKey,
                mappingFiltersMetadata,
                'group '
              );

              if (conversionResult.shouldUpdate) {
                set(newAction[input.inputKey][childIndex], childInput.inputKey, conversionResult.value);
              }
            });
          }
        });
      }
    });

    const freeSoloInput = find(postProcessingActionsById?.[action.actionId]?.inputs, {
      freeSolo: true,
    });

    const childrenInput = find(
      postProcessingActionsById?.[action.actionId]?.inputs,
      (input) => input.inputType === InputType.GROUP
    );

    if (freeSoloInput) {
      const options = getOptionsByValue(
        freeSoloInput.inputSource,
        [action.stain],
        index,
        action.actionInput,
        get(action, freeSoloInput.inputSourceDependentOn)
      );
      // if the value is not in the options, it means it is a new option value
      if (isEmpty(options) || !options[get(action, freeSoloInput.inputKey)]) {
        if (get(action, freeSoloInput.inputKey))
          set(newAction, 'newOptionValues', [get(action, freeSoloInput.inputKey)]);

        const attributeToMapInput = find(postProcessingActionsById?.[action.actionId]?.inputs, {
          inputKey: 'action_params.attribute_to_map',
        });

        if (attributeToMapInput && get(action, attributeToMapInput.inputKey)) {
          set(newAction, 'newOptionValueInput', get(action, attributeToMapInput.inputKey));
        }
      } else {
        delete newAction.newOptionValues;
        delete newAction.newOptionValueInput;
      }
    } else if (!isEmpty(childrenInput)) {
      const childrenFreeSoloInput = find(childrenInput?.children, {
        freeSolo: true,
      });

      if (childrenFreeSoloInput) {
        forEach(get(action, childrenInput.inputKey), (childInputValue, childIdx) => {
          const childValueKey = get(childInputValue, childrenFreeSoloInput.inputKey);
          const options = getOptionsByValue(
            childrenFreeSoloInput.inputSource,
            [action.stain],
            index,
            action.actionInput,
            get(action, childrenFreeSoloInput.inputSourceDependentOn)
          );

          // if the value is not in the options, it means it is a new option value
          if ((isEmpty(options) || !options[childValueKey]) && !isEmpty(childValueKey)) {
            set(newAction, `newOptionValues[${childIdx}]`, childValueKey);
          } else {
            set(newAction, `newOptionValues[${childIdx}]`, null);
          }
        });
      }
    } else {
      delete newAction.newOptionValues;
      delete newAction.newOptionValueInput;
    }

    return newAction;
  });
};

const getNewCellIdIfAlreadyCreated = (newCellsCreatedToSearch: Record<string, NewCell>, value: AutomaticCondition) => {
  const newCellKey = findKey(newCellsCreatedToSearch, (newCellCreated) => isEqual(newCellCreated.value, value));

  if (newCellKey) {
    newCellsCreatedToSearch[newCellKey].name = getNameByValue(value);
  }

  return newCellKey;
};

export const matchOrInsertCellValueToSessionNewCells = (
  cellValue: any,
  {
    stain,
    sessionNewCells,
    mappingFiltersMetadata,
  }: {
    stain: string;
    sessionNewCells: Record<string, NewCell>;
    mappingFiltersMetadata: MappingFilterMetadata[];
  }
) => {
  const newCellValue = convertLogicalQueryFromBackend(cellValue, mappingFiltersMetadata);

  let newCellId = getNewCellIdIfAlreadyCreated(sessionNewCells, newCellValue);

  if (!newCellId) {
    newCellId = uuidv4();
    sessionNewCells[newCellId] = {
      name: getNameByValue(newCellValue),
      stain: stain,
      value: newCellValue,
    };
  }

  return newCellId;
};

export const convertFeatureActionsFromBackend = (
  postProcessingActionsById: Dictionary<PostProcessingAction>,
  featureActions: PostProcessingActionCreated[],
  getMappingFiltersMetadataForLogicalQuery: (stain: string) => MappingFilterMetadata[],
  presetNewCells: Record<string, NewCell> = {}
) => {
  let newCellsCreated: Record<string, NewCell> = cloneDeep(presetNewCells || {});

  const convertedFeatureActions = map(cloneDeep(featureActions), (action) => {
    let newAction = cloneDeep(action);

    // First, handle logical query conversion for feature actions (similar to postprocessing)
    forEach(postProcessingActionsById?.[action.actionId]?.inputs, (input) => {
      if (input.inputType === InputType.LOGICAL_QUERY) {
        const logicalQueryValue = get(action, input.inputKey);
        if (logicalQueryValue) {
          const mappingFiltersMetadata = getMappingFiltersMetadataForLogicalQuery(
            action?.stain || first(action?.stains)
          );

          const conversionResult = convertLogicalQueryFromBackendWrapper(
            logicalQueryValue,
            action.actionName,
            input.inputKey,
            mappingFiltersMetadata,
            'feature '
          );

          if (conversionResult.shouldUpdate) {
            set(newAction, input.inputKey, conversionResult.value);
          }
        }
      }
    });

    // Then, handle intermediate cells input conversion
    const intermediateCellsInput = filter(postProcessingActionsById?.[action.actionId]?.inputs, {
      inputSource: InputSource.INTERMEDIATE_CELLS_FEATURES_PER_TARGET_STAIN,
    });

    if (!isEmpty(intermediateCellsInput)) {
      forEach(intermediateCellsInput, (input) => {
        const inputType = getInputType(input, action);
        const value = get(action, input.inputKey);
        const betweenStainsInput = isFeatureBetweenStains(action);
        const isTargetInput = input.inputKey === targetCellClassesInputKey;
        // understand why first is used here and not going over all stains
        const stain: string = betweenStainsInput
          ? isTargetInput
            ? first(get(action, TARGET_STAINS_INPUT_KEY))
            : first(get(action, NEIGHBOR_STAINS_INPUT_KEY))
          : first(get(action, 'stains'));

        const cellMappingFiltersMetadata = getMappingFiltersMetadataForLogicalQuery(stain);

        if (inputType === InputType.SELECT) {
          // if value is new cell
          if (isObject(value)) {
            const newCellId = matchOrInsertCellValueToSessionNewCells(value, {
              sessionNewCells: newCellsCreated,
              stain,
              mappingFiltersMetadata: cellMappingFiltersMetadata,
            });

            set(newAction, input.inputKey, newCellId);
          }
        } else if (inputType === InputType.MULTISELECT) {
          const newValues = map(value, (selectedOption) => {
            if (isObject(selectedOption)) {
              const newCellId = matchOrInsertCellValueToSessionNewCells(selectedOption, {
                sessionNewCells: newCellsCreated,
                stain,
                mappingFiltersMetadata: cellMappingFiltersMetadata,
              });

              return newCellId;
            }

            return selectedOption;
          });
          set(newAction, input.inputKey, newValues);
        }
      });
    }

    return newAction;
  });

  return {
    newCellsCreated,
    convertedFeatureActions,
  };
};

export const getAdditionalInputError = (
  inputValue: any,
  input: PostProcessingActionInput,
  action: PostProcessingActionCreated,
  slides: SlideInferenceResults[],
  selectedRegistrationsWithStains: RegistrationDetailsWithStains[]
) => {
  const specificInputError = getSpecificInputError(inputValue, input, action);
  if (specificInputError) return specificInputError;

  if (isFeatureBetweenStains(action) && input.inputKey === NEIGHBOR_STAINS_INPUT_KEY) {
    const selectedTargetStains: string[] = get(action, TARGET_STAINS_INPUT_KEY);
    const selectedTargetStainsRegistrations = filter(
      selectedRegistrationsWithStains,
      (registration: RegistrationDetailsWithStains) => {
        const isSourceStainingType = includes(selectedTargetStains, registration.sourceStainingType);
        const isTargetStainingType = includes(selectedTargetStains, registration.targetStainingType);
        return isSourceStainingType || isTargetStainingType;
      }
    );

    if (size(slides) > 0 && isEmpty(selectedRegistrationsWithStains)) {
      return 'Must select registrations for this feature';
    }

    const slidesByStain = groupBy(slides, 'stainingType');
    const validated = every(selectedTargetStains, (targetStain) =>
      validateTargetStainRegistrations(inputValue, slidesByStain[targetStain], selectedTargetStainsRegistrations)
    );

    if (!validated) {
      return 'Missing registration. Must select for each slide with target stain, a registration with the neighbor stain';
    }
  }

  return '';
};

const getArtifactOrchestrationIdFromUrl = (artifactUrlPattern?: string) => {
  if (!artifactUrlPattern) {
    return undefined;
  }

  return artifactUrlPattern.split('orchestration_id=')[1]?.split('&')[0] || undefined;
};

const getArtifactPathWithoutQuery = (artifactUrlPattern?: string) => artifactUrlPattern?.split('?')[0];

const normalizeArtifactFileNameForSignature = (artifactFileName?: string) => {
  if (!artifactFileName) {
    return artifactFileName;
  }

  return artifactFileName.replace(/^(saved|current)-pips-results$/, 'pips-results');
};

const getArtifactPathSignature = (artifactUrlPattern?: string) => {
  const artifactPath = getArtifactPathWithoutQuery(artifactUrlPattern);
  const artifactFileName = normalizeArtifactFileNameForSignature(artifactPath?.split('/').pop() || artifactPath);

  // Rebuild payloads can store a concrete slide-id filename while current artifacts use {slide_id} patterns.
  return artifactFileName?.replace(/^[^.]+\./, '');
};

const isSameOrchestrationResultArtifactUrlPattern = (
  currentArtifactUrlPattern?: string,
  artifactUrlPattern?: string
) => {
  if (currentArtifactUrlPattern === artifactUrlPattern) {
    return true;
  }

  const currentOrchestrationId = getArtifactOrchestrationIdFromUrl(currentArtifactUrlPattern);
  const savedOrchestrationId = getArtifactOrchestrationIdFromUrl(artifactUrlPattern);

  if (currentOrchestrationId || savedOrchestrationId) {
    return Boolean(
      currentOrchestrationId &&
        savedOrchestrationId &&
        currentOrchestrationId === savedOrchestrationId &&
        getArtifactPathSignature(currentArtifactUrlPattern) === getArtifactPathSignature(artifactUrlPattern)
    );
  }

  return getArtifactPathWithoutQuery(currentArtifactUrlPattern) === getArtifactPathWithoutQuery(artifactUrlPattern);
};

export const findByOrchestrationResultArtifactUrlPattern = <
  ResultType extends { orchestrations?: Array<{ orchestrationResultArtifactUrlPattern: string }> } = {
    orchestrations?: Array<{ orchestrationResultArtifactUrlPattern: string }>;
  }
>(
  results: ResultType[],
  orchestrationResultArtifactUrlPattern: string
) =>
  find(results, (resultsByName) =>
    some(resultsByName?.orchestrations, ({ orchestrationResultArtifactUrlPattern: currentArtifactUrlPattern }) =>
      isSameOrchestrationResultArtifactUrlPattern(currentArtifactUrlPattern, orchestrationResultArtifactUrlPattern)
    )
  );

export const findOrchestrationByArtifactUrlPattern = <
  OrchestrationType extends { orchestrationResultArtifactUrlPattern: string } = {
    orchestrationResultArtifactUrlPattern: string;
  }
>(
  orchestrations: OrchestrationType[] | undefined,
  orchestrationResultArtifactUrlPattern: string
) =>
  find(orchestrations, ({ orchestrationResultArtifactUrlPattern: currentArtifactUrlPattern }) =>
    isSameOrchestrationResultArtifactUrlPattern(currentArtifactUrlPattern, orchestrationResultArtifactUrlPattern)
  );

export const buildSelectedInferenceOrchestrationsFromArtifacts = (
  artifactUrlPatternsWithSlides: MapArtifactToSlides[],
  inferenceArtifacts: ModelInference[]
): OrchestrationBySlideByType => {
  const selectedInference: OrchestrationBySlideByType = {};

  forEach(artifactUrlPatternsWithSlides, ({ artifactUrlPattern, slideIds }) => {
    const currentModel = findByOrchestrationResultArtifactUrlPattern(inferenceArtifacts, artifactUrlPattern);
    const currentOrchestration = findOrchestrationByArtifactUrlPattern(
      currentModel?.orchestrations,
      artifactUrlPattern
    );

    if (!currentModel || !currentOrchestration) {
      return;
    }

    forEach(slideIds, (slideId) => {
      selectedInference[slideId] = {
        ...selectedInference[slideId],
        [currentModel.modelType]: {
          orchestration: currentOrchestration,
          model: currentModel,
        },
      };
    });
  });

  return selectedInference;
};

export const doesSpecificModelExist = (
  stain: string,
  specificModelType: string,
  selectedOrchestrations: OrchestrationBySlideByType,
  selectedAssignments: AssignmentByStain,
  slidesById: Dictionary<SlideInferenceResults>
): boolean => {
  const existInSelectedOrchestrations = some(selectedOrchestrations, (selectedOrchestration: any, slideId: string) => {
    return some(selectedOrchestration, (orchestrationData: any, modelType: string) => {
      return modelType === specificModelType && slidesById[slideId]?.stainingType === stain;
    });
  });

  const existInSelectedAssignments = some(selectedAssignments[stain], (assignment: any) => {
    return assignment.modelType === specificModelType;
  });

  return existInSelectedOrchestrations || existInSelectedAssignments;
};
